import { memo, useState } from 'react';

import classNames from 'classnames';
import { clamp, range } from 'lodash';

import styles from './styles.module.scss';

interface RatingIndicatorProps {
  value: number;
}

interface StarProps {
  completion?: number;
  onEnter?: () => void;
  onClick?: () => void;
}

const Star = memo(({ completion = 1, onEnter, onClick }: StarProps) => {
  const sizePx = 16;

  return (
    <svg
      width={sizePx}
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      onMouseEnter={onEnter}
      onClick={onClick}
      className={classNames(styles['star'], styles['rating__base__star'])}
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        clipPath={`path("M7.1045 1.94508C7.47224 1.20515 8.52778 1.20515 8.89551 1.94508L10.8291 5.83564L15.1382 6.45744C15.9606 6.57611 16.2879 7.5878 15.6909 8.16571L12.5775 11.1794L13.314 15.4436C13.4549 16.2596 12.5998 16.8835 11.8658 16.5003L8.00001 14.482L4.13423 16.5003C3.4002 16.8835 2.54509 16.2596 2.68602 15.4436L3.42251 11.1794L0.309125 8.16571C-0.287893 7.5878 0.0394307 6.57611 0.861817 6.45744L5.17096 5.83564L7.1045 1.94508Z")`}
      >
        <rect x="0" y="0" width={sizePx} height="17" className={styles['star--background']} />

        <rect
          x="0"
          y="0"
          width={clamp(completion, 0, 1) * sizePx}
          height="17"
          className={styles['star--foreground']}
        />
      </g>
    </svg>
  );
});

interface RatingBaseProps {
  value: number;
  mode: 'indicator' | 'control';
  starClassName?: string;
  onClickStar?: (position: number) => void;
  onEnterStar?: (position: number) => void;
  onLeave?: () => void;
}

const RatingBase = memo(({ value, mode, onEnterStar, onClickStar, onLeave }: RatingBaseProps) => {
  return (
    <div className={styles['rating__base']} onMouseLeave={onLeave} data-mode={mode}>
      {range(1, 6).map((position) => {
        return (
          <Star
            completion={value - position + 1}
            onEnter={() => onEnterStar?.(position)}
            onClick={() => onClickStar?.(position)}
            key={position}
          />
        );
      })}
    </div>
  );
});

const RatingIndicator = memo(({ value }: RatingIndicatorProps) => {
  return <RatingBase mode="indicator" value={value} />;
});

interface RatingControlProps {
  value: number;
  onChange?: (value: number) => void;
}

const RatingControl = memo(({ value, onChange }: RatingControlProps) => {
  const [preliminaryValue, setPreliminaryValue] = useState<number | null>(null);

  return (
    <RatingBase
      mode="control"
      value={preliminaryValue || value}
      onEnterStar={setPreliminaryValue}
      onClickStar={onChange}
      onLeave={() => setPreliminaryValue(0)}
    />
  );
});

const Rating = {
  Indicator: RatingIndicator,
  Control: RatingControl,
} as const;

export { Rating, type RatingIndicatorProps };
